import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { 
  fetchUserDropdowns, 
  selectLastUserDropdownFetch,
  selectUserDropdowns, 
  selectDropdownLoading, 
  selectDropdownError 
} from "../redux/slices/dropdownSlice";

const sortOptionsByDisplayName = (options = []) => {
  if (!Array.isArray(options)) {
    return [];
  }

  return [...options].sort((a, b) =>
    (a?.displayName || "").localeCompare(b?.displayName || "", undefined, {
      sensitivity: "base",
    })
  );
};

/**
 * Custom hook to fetch and access user dropdowns from MS Graph API
 * Uses Redux for caching and preventing duplicate API calls
 * 
 * IMPORTANT: This hook now uses Redux to cache data and prevent race conditions.
 * Multiple components can use this hook without causing duplicate API calls.
 * Data is cached for 4 hours (configurable in dropdownSlice).
 */
const useUserDropdowns = () => {
  const dispatch = useDispatch();
  const { instance, accounts } = useMsal();
  
  // Get data from Redux store
  const userDropdowns = useSelector(selectUserDropdowns);
  const lastUserDropdownFetch = useSelector(selectLastUserDropdownFetch);
  const loading = useSelector(selectDropdownLoading);
  const error = useSelector(selectDropdownError);

  const sortedUserDropdowns = useMemo(
    () => ({
      mechOptions: sortOptionsByDisplayName(userDropdowns.mechOptions),
      appsOptions: sortOptionsByDisplayName(userDropdowns.appsOptions),
      elecOptions: sortOptionsByDisplayName(userDropdowns.elecOptions),
      controlOptions: sortOptionsByDisplayName(userDropdowns.controlOptions),
      salesOptions: sortOptionsByDisplayName(userDropdowns.salesOptions),
      adminOptions: sortOptionsByDisplayName(userDropdowns.adminOptions),
    }),
    [userDropdowns]
  );

  // Fetch user dropdowns on mount if MSAL is ready
  // Redux will handle caching and prevent duplicate calls
  useEffect(() => {
    if (instance && accounts && accounts.length > 0 && !lastUserDropdownFetch) {
      dispatch(fetchUserDropdowns({ instance, accounts }));
    }
  }, [dispatch, instance, accounts, lastUserDropdownFetch]);

  // Return data in the same format as before for backward compatibility
  return {
    mechOptions: sortedUserDropdowns.mechOptions,
    appsOptions: sortedUserDropdowns.appsOptions,
    elecOptions: sortedUserDropdowns.elecOptions,
    controlOptions: sortedUserDropdowns.controlOptions,
    salesOptions: sortedUserDropdowns.salesOptions,
    adminOptions: sortedUserDropdowns.adminOptions,
    loading,
    error,
  };
};

export default useUserDropdowns;